import React, { useState, useEffect, useCallback } from "react";

/* ═══════════════════════════════════════════════════════════════════════
   HOME HQ PET SYSTEM
   8-bit virtual pets — eggs → hatch → evolve → collect
   ═══════════════════════════════════════════════════════════════════════ */

// ── Evolution thresholds ─────────────────────────────────────
export const STAGE_NAMES = ["Egg", "Baby", "Juvenile", "Adult", "Legendary"];
export const STAGE_THRESHOLDS = [0, 5, 25, 75, 200];

// ── Species definitions ──────────────────────────────────────
// Each species has hints (egg pattern clue), and pixel art per stage
export const SPECIES = {
  dragon:   { name: "Dragon",   hint: "fire",  emoji: "🐉" },
  cat:      { name: "Cat",      hint: "soft",  emoji: "🐱" },
  frog:     { name: "Frog",     hint: "swamp", emoji: "🐸" },
  bird:     { name: "Bird",     hint: "sky",   emoji: "🐦" },
  bunny:    { name: "Bunny",    hint: "fluff", emoji: "🐰" },
  fox:      { name: "Fox",      hint: "wild",  emoji: "🦊" },
  turtle:   { name: "Turtle",   hint: "shell", emoji: "🐢" },
  penguin:  { name: "Penguin",  hint: "ice",   emoji: "🐧" },
};

// ── Egg patterns ─────────────────────────────────────────────
export const EGG_PATTERNS = [
  { id: "spots",   label: "Spotted" },
  { id: "stripes", label: "Striped" },
  { id: "zigzag",  label: "Zigzag" },
  { id: "stars",   label: "Starry" },
  { id: "swirl",   label: "Swirl" },
  { id: "plain",   label: "Plain" },
];

// Map egg color hints to likely species (50% chance of this, 50% random)
const COLOR_SPECIES_MAP = {
  "#E85454": "dragon",  "#FF6B35": "fox",
  "#5BAD6F": "frog",    "#5B8DD9": "bird",
  "#E8C84A": "cat",     "#E87B9C": "bunny",
  "#2A8888": "turtle",  "#6A7BCC": "penguin",
};

// Colour variations per species — each pet gets a random palette
const SPECIES_PALETTES = {
  dragon: [
    { main: "#C83030", dark: "#A02020", accent: "#F0C860", label: "Red" },
    { main: "#3060C8", dark: "#2040A0", accent: "#90C0F0", label: "Blue" },
    { main: "#30A830", dark: "#208020", accent: "#C0F080", label: "Green" },
    { main: "#8830C8", dark: "#6020A0", accent: "#D0A0F0", label: "Purple" },
    { main: "#1A1A2E", dark: "#111122", accent: "#4488FF", label: "Shadow" },
  ],
  cat: [
    { main: "#E8A040", dark: "#C88030", accent: "#F0D890", label: "Orange Tabby" },
    { main: "#9E9E9E", dark: "#707070", accent: "#D0D0D0", label: "Grey" },
    { main: "#E8D8C0", dark: "#D0C0A0", accent: "#FFF0E0", label: "Cream" },
    { main: "#1A1A1A", dark: "#111111", accent: "#404040", label: "Black" },
    { main: "#C8A060", dark: "#A88040", accent: "#E8D0A0", label: "Ginger" },
  ],
  frog: [
    { main: "#2A8830", dark: "#1A6820", accent: "#A0D8A0", label: "Green" },
    { main: "#3080C0", dark: "#2060A0", accent: "#90C8E8", label: "Blue" },
    { main: "#C83030", dark: "#A02020", accent: "#F0A0A0", label: "Poison" },
    { main: "#E8C830", dark: "#C8A020", accent: "#F0E080", label: "Golden" },
  ],
  bird: [
    { main: "#3A68C8", dark: "#2850A8", accent: "#F0E8D0", label: "Blue Jay" },
    { main: "#C83030", dark: "#A02020", accent: "#F0D0C0", label: "Cardinal" },
    { main: "#E8C830", dark: "#C8A020", accent: "#FFF8E0", label: "Canary" },
    { main: "#30A868", dark: "#208850", accent: "#C0F0D0", label: "Parrot" },
  ],
  bunny: [
    { main: "#E8A0B8", dark: "#D888A0", accent: "#FFB8C8", label: "Pink" },
    { main: "#E8E0D0", dark: "#D0C8B8", accent: "#FFF8F0", label: "White" },
    { main: "#B8885A", dark: "#986840", accent: "#D8C0A0", label: "Brown" },
    { main: "#808080", dark: "#606060", accent: "#B0B0B0", label: "Grey" },
  ],
  fox: [
    { main: "#D85820", dark: "#B84818", accent: "#F8F0E0", label: "Red" },
    { main: "#808080", dark: "#606060", accent: "#E0E0E0", label: "Silver" },
    { main: "#E8D0A0", dark: "#C8B080", accent: "#FFF8F0", label: "Fennec" },
    { main: "#1A1A2E", dark: "#111122", accent: "#D0D0D0", label: "Arctic" },
  ],
  turtle: [
    { main: "#1A6848", dark: "#105838", accent: "#8A6440", label: "Green" },
    { main: "#6A4420", dark: "#503218", accent: "#3A6848", label: "Brown" },
    { main: "#2A5888", dark: "#1A4068", accent: "#7A8850", label: "Sea" },
    { main: "#C83030", dark: "#A02020", accent: "#8A6440", label: "Fire" },
  ],
  penguin: [
    { main: "#1A1A2E", dark: "#111122", accent: "#F0F0F0", label: "Classic" },
    { main: "#2A3858", dark: "#1A2840", accent: "#E8E0D0", label: "Emperor" },
    { main: "#4A6888", dark: "#3A5068", accent: "#F0F0F0", label: "Little Blue" },
    { main: "#5A3828", dark: "#3A2818", accent: "#F0E8D0", label: "Rockhopper" },
  ],
};

function pickRandomSpecies(hintedSpecies) {
  const allSpecies = Object.keys(SPECIES);
  // 50% chance of getting the hinted species, 50% pure random
  if (Math.random() < 0.5) return hintedSpecies;
  return allSpecies[Math.floor(Math.random() * allSpecies.length)];
}

function pickRandomPalette(species) {
  const palettes = SPECIES_PALETTES[species] || SPECIES_PALETTES.cat;
  return palettes[Math.floor(Math.random() * palettes.length)];
}

const EGG_PRESETS = [
  { color: "#E85454", accent: "#FF9944", pattern: "zigzag" },
  { color: "#5BAD6F", accent: "#A0D8A0", pattern: "spots" },
  { color: "#5B8DD9", accent: "#A0C8F0", pattern: "stripes" },
  { color: "#E8C84A", accent: "#FFF0A0", pattern: "stars" },
  { color: "#E87B9C", accent: "#FFB8D0", pattern: "spots" },
  { color: "#2A8888", accent: "#80D8D8", pattern: "swirl" },
  { color: "#FF6B35", accent: "#FFB088", pattern: "zigzag" },
  { color: "#6A7BCC", accent: "#B0B8E8", pattern: "stripes" },
];

// ── Pixel helper ─────────────────────────────────────────────
const B = ({ x, y, w = 1, h = 1, fill }) => (
  <rect x={x} y={y} width={w} height={h} fill={fill} />
);

// ── Mood expressions ─────────────────────────────────────────
export function getMoodForProgress(done, total) {
  if (total === 0) return "neutral";
  const pct = done / total;
  if (pct >= 1) return "celebrating";
  if (pct >= 0.75) return "excited";
  if (pct >= 0.4) return "happy";
  if (pct > 0) return "neutral";
  const hour = new Date().getHours();
  if (hour >= 14) return "sleepy";
  return "neutral";
}

// ── Egg Renderer ─────────────────────────────────────────────
function renderEgg(color, accent, pattern, size) {
  return (
    <svg width={size} height={size * 1.2} viewBox="0 0 20 24" style={{ imageRendering: "pixelated" }}>
      {/* Egg body */}
      <B x={6} y={2} w={8} h={2} fill={color} />
      <B x={4} y={4} w={12} h={2} fill={color} />
      <B x={3} y={6} w={14} h={4} fill={color} />
      <B x={3} y={10} w={14} h={4} fill={color} />
      <B x={4} y={14} w={12} h={2} fill={color} />
      <B x={5} y={16} w={10} h={2} fill={color} />
      <B x={6} y={18} w={8} h={2} fill={color} />
      {/* Highlight */}
      <B x={6} y={3} w={3} h={1} fill="#FFFFFF40" />
      <B x={5} y={5} w={2} h={2} fill="#FFFFFF30" />
      {/* Shadow */}
      <B x={13} y={8} w={2} h={6} fill="#00000020" />
      <B x={12} y={15} w={2} h={2} fill="#00000015" />

      {/* Pattern overlay */}
      {pattern === "spots" && (<g>
        <B x={7} y={6} w={2} h={2} fill={accent} />
        <B x={12} y={8} w={2} h={2} fill={accent} />
        <B x={5} y={10} w={2} h={2} fill={accent} />
        <B x={10} y={12} w={2} h={2} fill={accent} />
        <B x={7} y={14} w={2} h={2} fill={accent} />
      </g>)}
      {pattern === "stripes" && (<g>
        <B x={4} y={6} w={12} h={1} fill={accent} />
        <B x={3} y={10} w={14} h={1} fill={accent} />
        <B x={4} y={14} w={12} h={1} fill={accent} />
      </g>)}
      {pattern === "zigzag" && (<g>
        <B x={5} y={6} w={2} h={2} fill={accent} /><B x={7} y={7} w={2} h={2} fill={accent} />
        <B x={9} y={6} w={2} h={2} fill={accent} /><B x={11} y={7} w={2} h={2} fill={accent} />
        <B x={5} y={12} w={2} h={2} fill={accent} /><B x={7} y={13} w={2} h={2} fill={accent} />
        <B x={9} y={12} w={2} h={2} fill={accent} /><B x={11} y={13} w={2} h={2} fill={accent} />
      </g>)}
      {pattern === "stars" && (<g>
        <B x={7} y={5} w={1} h={1} fill={accent} /><B x={6} y={6} w={1} h={1} fill={accent} />
        <B x={8} y={6} w={1} h={1} fill={accent} /><B x={7} y={7} w={1} h={1} fill={accent} />
        <B x={12} y={9} w={1} h={1} fill={accent} /><B x={11} y={10} w={1} h={1} fill={accent} />
        <B x={13} y={10} w={1} h={1} fill={accent} /><B x={12} y={11} w={1} h={1} fill={accent} />
        <B x={6} y={13} w={1} h={1} fill={accent} /><B x={5} y={14} w={1} h={1} fill={accent} />
        <B x={7} y={14} w={1} h={1} fill={accent} /><B x={6} y={15} w={1} h={1} fill={accent} />
      </g>)}
      {pattern === "swirl" && (<g>
        <B x={7} y={5} w={1} h={1} fill={accent} /><B x={8} y={6} w={1} h={1} fill={accent} />
        <B x={9} y={7} w={1} h={1} fill={accent} /><B x={10} y={8} w={1} h={1} fill={accent} />
        <B x={10} y={9} w={1} h={1} fill={accent} /><B x={9} y={10} w={1} h={1} fill={accent} />
        <B x={8} y={11} w={1} h={1} fill={accent} /><B x={7} y={12} w={1} h={1} fill={accent} />
        <B x={6} y={13} w={1} h={1} fill={accent} /><B x={6} y={14} w={1} h={1} fill={accent} />
        <B x={7} y={15} w={1} h={1} fill={accent} />
      </g>)}
    </svg>
  );
}

// ── Pet Renderers (by species and stage) ─────────────────────
// Each pet: viewBox 0 0 20 20, pixel grid
function renderPetPixels(species, stage, mood, palette) {
  // Use custom palette if provided, otherwise fall back to defaults
  const pm = palette?.main;
  const pd = palette?.dark;
  const pa = palette?.accent;
  // Eye style based on mood
  const eyeL = { x: 6, y: 7 };
  const eyeR = { x: 11, y: 7 };
  const mouthY = 10;

  const eyes = (color = "#111") => {
    if (mood === "celebrating") return (<g>
      <B x={eyeL.x} y={eyeL.y} w={2} h={1} fill={color} /><B x={eyeR.x} y={eyeR.y} w={2} h={1} fill={color} />
      <B x={eyeL.x} y={eyeL.y - 1} w={1} h={1} fill={color} /><B x={eyeR.x + 1} y={eyeR.y - 1} w={1} h={1} fill={color} />
    </g>);
    if (mood === "excited") return (<g>
      <B x={eyeL.x} y={eyeL.y} w={2} h={2} fill={color} /><B x={eyeR.x} y={eyeR.y} w={2} h={2} fill={color} />
      <B x={eyeL.x + 1} y={eyeL.y} w={1} h={1} fill="#FFF" /><B x={eyeR.x + 1} y={eyeR.y} w={1} h={1} fill="#FFF" />
    </g>);
    if (mood === "sleepy") return (<g>
      <B x={eyeL.x} y={eyeL.y + 1} w={2} h={1} fill={color} /><B x={eyeR.x} y={eyeR.y + 1} w={2} h={1} fill={color} />
    </g>);
    if (mood === "sad") return (<g>
      <B x={eyeL.x} y={eyeL.y} w={2} h={2} fill={color} /><B x={eyeR.x} y={eyeR.y} w={2} h={2} fill={color} />
      <B x={eyeL.x} y={eyeL.y} w={1} h={1} fill="#4488CC40" /><B x={eyeR.x} y={eyeR.y} w={1} h={1} fill="#4488CC40" />
    </g>);
    // neutral / happy
    return (<g>
      <B x={eyeL.x} y={eyeL.y} w={2} h={2} fill={color} /><B x={eyeR.x} y={eyeR.y} w={2} h={2} fill={color} />
    </g>);
  };

  const mouth = () => {
    if (mood === "celebrating" || mood === "happy") return <B x={7} y={mouthY} w={4} h={1} fill="#E06060" />;
    if (mood === "sad") return (<g><B x={8} y={mouthY + 1} w={2} h={1} fill="#888" /><B x={7} y={mouthY} w={1} h={1} fill="#888" /><B x={10} y={mouthY} w={1} h={1} fill="#888" /></g>);
    if (mood === "sleepy") return (<g><B x={8} y={mouthY} w={3} h={1} fill="#888" /></g>);
    return <B x={8} y={mouthY} w={2} h={1} fill="#888" />;
  };

  const pets = {
    dragon: (s) => {
      const c = pm || (s >= 3 ? "#C83030" : s >= 2 ? "#D44040" : "#E86060");
      const d = pd || (s >= 3 ? "#A02020" : s >= 2 ? "#B03030" : "#C84848");
      const belly = pa || "#F0C860";
      return (<g>
        <B x={5} y={4} w={8} h={8} fill={c} /><B x={4} y={6} w={1} h={4} fill={c} /><B x={13} y={6} w={1} h={4} fill={d} />
        {/* Head spikes */}
        <B x={5} y={3} w={2} h={1} fill={c} /><B x={11} y={3} w={2} h={1} fill={d} />
        {s >= 2 && <g><B x={6} y={2} w={1} h={1} fill="#FF8800" /><B x={11} y={2} w={1} h={1} fill="#FF6600" /></g>}
        {s >= 3 && <g><B x={5} y={1} w={2} h={2} fill="#FF6600" /><B x={11} y={1} w={2} h={2} fill="#DD4400" /></g>}
        {/* Wings for adult+ */}
        {s >= 3 && <g><B x={2} y={5} w={2} h={4} fill={c+"B0"} /><B x={14} y={5} w={2} h={4} fill={d+"B0"} /></g>}
        {s >= 4 && <g><B x={1} y={4} w={2} h={5} fill={c+"90"} /><B x={15} y={4} w={2} h={5} fill={d+"90"} /></g>}
        {/* Belly */}
        <B x={7} y={8} w={4} h={3} fill={belly} />
        {/* Feet */}
        <B x={5} y={12} w={3} h={2} fill={d} /><B x={10} y={12} w={3} h={2} fill={d} />
        {/* Tail */}
        <B x={13} y={10} w={2} h={1} fill={d} /><B x={14} y={9} w={2} h={1} fill={d} />
        {s >= 4 && <B x={15} y={8} w={2} h={1} fill="#FF6600" />}
        {eyes()}{mouth()}
        {/* Fire breath for legendary */}
        {s >= 4 && mood === "celebrating" && <g><B x={3} y={8} w={1} h={1} fill="#FF4400" /><B x={2} y={9} w={1} h={1} fill="#FF8800" /><B x={1} y={8} w={1} h={1} fill="#FFCC00" /></g>}
      </g>);
    },

    cat: (s) => {
      const c = pm || (s >= 3 ? "#E8A040" : s >= 2 ? "#D8A060" : "#E8C090");
      const d = pd || (s >= 3 ? "#C88030" : s >= 2 ? "#B88050" : "#D0A878");
      return (<g>
        <B x={5} y={5} w={8} h={7} fill={c} /><B x={4} y={6} w={1} h={5} fill={c} /><B x={13} y={6} w={1} h={5} fill={d} />
        {/* Ears */}
        <B x={5} y={3} w={2} h={2} fill={c} /><B x={11} y={3} w={2} h={2} fill={d} />
        <B x={6} y={4} w={1} h={1} fill="#F0A0A0" /><B x={11} y={4} w={1} h={1} fill="#E09090" />
        {/* Stripes for evolved */}
        {s >= 2 && <g><B x={6} y={5} w={6} h={1} fill={d} /><B x={7} y={8} w={4} h={1} fill={d} /></g>}
        {s >= 3 && <g><B x={5} y={6} w={1} h={1} fill={d} /><B x={12} y={6} w={1} h={1} fill={d+"CC"} /></g>}
        {/* Whiskers for adult+ */}
        {s >= 3 && <g><B x={3} y={8} w={2} h={1} fill="#888" /><B x={13} y={8} w={2} h={1} fill="#888" /></g>}
        {/* Feet */}
        <B x={5} y={12} w={3} h={2} fill={d} /><B x={10} y={12} w={3} h={2} fill={d} />
        {/* Tail */}
        <B x={13} y={8} w={2} h={1} fill={c} /><B x={14} y={7} w={2} h={1} fill={c} /><B x={15} y={6} w={1} h={1} fill={c} />
        {s >= 4 && <B x={16} y={5} w={1} h={2} fill={c} />}
        {eyes()}{mouth()}
        {/* Crown for legendary */}
        {s >= 4 && <g><B x={7} y={2} w={4} h={1} fill="#FFD700" /><B x={8} y={1} w={2} h={1} fill="#FFD700" /><B x={7} y={1} w={1} h={1} fill="#FFD700" /><B x={10} y={1} w={1} h={1} fill="#FFD700" /></g>}
      </g>);
    },

    frog: (s) => {
      const c = pm || (s >= 3 ? "#2A8830" : s >= 2 ? "#3A9840" : "#5ABB60");
      const d = pd || (s >= 3 ? "#1A6820" : s >= 2 ? "#2A7830" : "#48A050");
      const belly = pa || "#A0D8A0";
      return (<g>
        <B x={4} y={5} w={10} h={7} fill={c} /><B x={3} y={7} w={1} h={3} fill={c} /><B x={14} y={7} w={1} h={3} fill={d} />
        {/* Big eyes on top */}
        <B x={4} y={3} w={4} h={3} fill="#FFF" /><B x={10} y={3} w={4} h={3} fill="#FFF" />
        <B x={5} y={4} w={2} h={2} fill="#111" /><B x={11} y={4} w={2} h={2} fill="#111" />
        {/* Belly */}
        <B x={6} y={8} w={6} h={3} fill={belly} />
        {/* Feet */}
        <B x={3} y={12} w={4} h={2} fill={d} /><B x={11} y={12} w={4} h={2} fill={d} />
        {s >= 2 && <g><B x={2} y={12} w={1} h={1} fill={d} /><B x={15} y={12} w={1} h={1} fill={d} /></g>}
        {/* Spots for evolved */}
        {s >= 3 && <g><B x={5} y={6} w={2} h={1} fill={d} /><B x={11} y={7} w={2} h={1} fill={d} /></g>}
        {mouth()}
        {/* Fly for legendary */}
        {s >= 4 && <g><B x={2} y={6} w={1} h={1} fill="#333" /><B x={1} y={5} w={1} h={1} fill="#AAA" /></g>}
        {/* Lily pad for legendary */}
        {s >= 4 && <g><B x={3} y={14} w={12} h={1} fill="#3A8" /><B x={4} y={15} w={10} h={1} fill="#2A7" /></g>}
      </g>);
    },

    bird: (s) => {
      const c = pm || (s >= 3 ? "#3A68C8" : s >= 2 ? "#5088D8" : "#70A8E8");
      const d = pd || (s >= 3 ? "#2850A8" : s >= 2 ? "#4070C0" : "#5890D0");
      return (<g>
        <B x={5} y={4} w={8} h={7} fill={c} /><B x={4} y={6} w={1} h={4} fill={c} /><B x={13} y={6} w={1} h={4} fill={d} />
        {/* Head crest */}
        <B x={8} y={2} w={2} h={2} fill={c} /><B x={9} y={1} w={2} h={2} fill={"#E8C830"} />
        {/* Wings */}
        <B x={3} y={6} w={2} h={3} fill={d} /><B x={13} y={6} w={2} h={3} fill={d+"CC"} />
        {s >= 2 && <g><B x={2} y={7} w={1} h={2} fill={d} /><B x={15} y={7} w={1} h={2} fill={d+"CC"} /></g>}
        {s >= 3 && <g><B x={1} y={7} w={1} h={2} fill={d+"90"} /><B x={16} y={7} w={1} h={2} fill={d+"80"} /></g>}
        {/* Belly */}
        <B x={7} y={8} w={4} h={2} fill="#F0E8D0" />
        {/* Beak */}
        <B x={5} y={8} w={2} h={1} fill="#E8A030" />
        {/* Feet */}
        <B x={6} y={11} w={2} h={2} fill="#E8A030" /><B x={10} y={11} w={2} h={2} fill="#D09028" />
        {/* Tail */}
        <B x={13} y={5} w={2} h={2} fill={d} />
        {s >= 4 && <g><B x={14} y={4} w={2} h={3} fill="#E8C830" /><B x={15} y={3} w={1} h={2} fill="#E85050" /></g>}
        {eyes()}{mouth()}
      </g>);
    },

    bunny: (s) => {
      const c = pm || (s >= 3 ? "#E8A0B8" : s >= 2 ? "#F0B0C8" : "#F8C8D8");
      const d = pd || (s >= 3 ? "#D888A0" : s >= 2 ? "#E098B0" : "#F0B0C0");
      return (<g>
        <B x={5} y={5} w={8} h={7} fill={c} /><B x={4} y={7} w={1} h={4} fill={c} /><B x={13} y={7} w={1} h={4} fill={d} />
        {/* Long ears */}
        <B x={6} y={0} w={2} h={5} fill={c} /><B x={10} y={0} w={2} h={5} fill={d} />
        <B x={6} y={1} w={1} h={3} fill="#F0A0B0" /><B x={11} y={1} w={1} h={3} fill="#E090A0" />
        {s >= 2 && <g><B x={6} y={-1} w={2} h={1} fill={c} /><B x={10} y={-1} w={2} h={1} fill={d} /></g>}
        {/* Cheeks */}
        <B x={5} y={8} w={2} h={2} fill="#FFB8C8" /><B x={11} y={8} w={2} h={2} fill="#F0A8B8" />
        {/* Nose */}
        <B x={8} y={9} w={2} h={1} fill="#E08090" />
        {/* Feet */}
        <B x={5} y={12} w={3} h={2} fill={d} /><B x={10} y={12} w={3} h={2} fill={d} />
        {/* Tail */}
        <B x={13} y={10} w={2} h={2} fill="#FFF" />
        {s >= 4 && <B x={14} y={9} w={2} h={2} fill="#FFE0F0" />}
        {eyes()}{mouth()}
        {/* Bow for legendary */}
        {s >= 4 && <g><B x={5} y={5} w={2} h={1} fill="#FF4488" /><B x={4} y={4} w={1} h={1} fill="#FF4488" /><B x={7} y={4} w={1} h={1} fill="#FF4488" /><B x={5} y={4} w={2} h={1} fill="#FF6699" /></g>}
      </g>);
    },

    fox: (s) => {
      const c = pm || (s >= 3 ? "#D85820" : s >= 2 ? "#E06830" : "#E88040");
      const d = pd || (s >= 3 ? "#B84818" : s >= 2 ? "#C85828" : "#D06838");
      return (<g>
        <B x={5} y={4} w={8} h={8} fill={c} /><B x={4} y={6} w={1} h={5} fill={c} /><B x={13} y={6} w={1} h={5} fill={d} />
        {/* Ears */}
        <B x={5} y={2} w={2} h={2} fill={c} /><B x={11} y={2} w={2} h={2} fill={d} />
        <B x={5} y={2} w={1} h={1} fill="#111" /><B x={12} y={2} w={1} h={1} fill="#111" />
        {/* White face */}
        <B x={7} y={7} w={4} h={4} fill="#F8F0E0" />
        {/* White tail tip */}
        <B x={14} y={8} w={2} h={2} fill={c} /><B x={15} y={7} w={2} h={2} fill="#F8F0E0" />
        {s >= 3 && <B x={16} y={6} w={1} h={2} fill="#F8F0E0" />}
        {/* Feet */}
        <B x={5} y={12} w={3} h={2} fill="#222" /><B x={10} y={12} w={3} h={2} fill="#1A1A1A" />
        {eyes()}{mouth()}
        {/* Scarf for legendary */}
        {s >= 4 && <g><B x={5} y={11} w={8} h={1} fill="#D83030" /><B x={4} y={11} w={1} h={3} fill="#D83030" /></g>}
      </g>);
    },

    turtle: (s) => {
      const c = pm || (s >= 3 ? "#1A6848" : s >= 2 ? "#2A7858" : "#3A8868");
      const d = pd || (s >= 3 ? "#105838" : s >= 2 ? "#1A6848" : "#2A7858");
      const sh = pa || (s >= 3 ? "#6A4420" : s >= 2 ? "#7A5430" : "#8A6440");
      return (<g>
        {/* Shell */}
        <B x={4} y={5} w={10} h={7} fill={sh} />
        <B x={5} y={4} w={8} h={1} fill={sh} />
        <B x={6} y={6} w={2} h={2} fill={sh+"88"} /><B x={10} y={6} w={2} h={2} fill={sh+"88"} />
        <B x={8} y={9} w={2} h={2} fill={sh+"88"} />
        {s >= 3 && <B x={5} y={3} w={8} h={1} fill={sh} />}
        {/* Head */}
        <B x={2} y={6} w={3} h={4} fill={c} />
        {/* Eyes */}
        <B x={2} y={7} w={1} h={1} fill="#FFF" /><B x={2} y={7} w={1} h={1} fill="#111" />
        {/* Feet */}
        <B x={4} y={12} w={3} h={2} fill={c} /><B x={11} y={12} w={3} h={2} fill={d} />
        {/* Tail */}
        <B x={14} y={10} w={2} h={1} fill={c} />
        {mouth()}
        {/* Crown for legendary */}
        {s >= 4 && <g><B x={2} y={5} w={3} h={1} fill="#FFD700" /><B x={3} y={4} w={1} h={1} fill="#FFD700" /></g>}
      </g>);
    },

    penguin: (s) => {
      const c = pm || "#1A1A2E";
      const d = pd || "#111122";
      const belly = pa || "#F0F0F0";
      return (<g>
        <B x={5} y={4} w={8} h={8} fill={c} /><B x={4} y={6} w={1} h={5} fill={c} /><B x={13} y={6} w={1} h={5} fill={d} />
        {/* White belly */}
        <B x={7} y={6} w={4} h={5} fill={belly} />
        {/* Head shape */}
        <B x={6} y={3} w={6} h={1} fill={c} />
        {/* Wings */}
        <B x={3} y={6} w={2} h={4} fill={c} /><B x={13} y={6} w={2} h={4} fill={d} />
        {s >= 2 && <g><B x={2} y={7} w={1} h={3} fill={c} /><B x={15} y={7} w={1} h={3} fill={d} /></g>}
        {/* Beak */}
        <B x={8} y={8} w={2} h={1} fill="#E8A030" />
        {/* Feet */}
        <B x={6} y={12} w={3} h={2} fill="#E8A030" /><B x={10} y={12} w={3} h={2} fill="#D09028" />
        {eyes("#FFF")}{mouth()}
        {/* Bow tie for legendary */}
        {s >= 4 && <g><B x={8} y={5} w={2} h={1} fill="#D83030" /><B x={7} y={5} w={1} h={1} fill="#D83030" /><B x={10} y={5} w={1} h={1} fill="#D83030" /></g>}
        {/* Top hat for legendary */}
        {s >= 4 && <g><B x={6} y={2} w={6} h={1} fill="#111" /><B x={7} y={0} w={4} h={2} fill="#111" /></g>}
      </g>);
    },
  };

  const renderFn = pets[species] || pets.cat;
  return renderFn(stage);
}

// ── Main Pet Display Component ───────────────────────────────
export function PetDisplay({ pet, size = 64, mood = "neutral" }) {
  if (!pet) return null;

  if (pet.stage === 0) {
    // Egg gets a gentle wobble
    return (
      <div style={{ animation: "eggWobble 3s ease-in-out infinite" }}>
        {renderEgg(pet.egg_color, pet.egg_accent, pet.egg_pattern, size)}
      </div>
    );
  }

  const stageScale = [0, 0.65, 0.8, 1.0, 1.05][pet.stage] || 1;
  const viewSize = 18;
  const offset = (viewSize * (1 - stageScale)) / 2;
  const isLegendary = pet.stage >= 4;

  // Species-specific idle animation name
  const idleAnim = {
    dragon: "petBreathe", bird: "petHop", bunny: "petHop",
    cat: "petBreathe", frog: "petBreathe", fox: "petBreathe",
    turtle: "petBreathe", penguin: "petWaddle",
  }[pet.species] || "petBreathe";

  const idleDur = {
    dragon: "2.5s", bird: "1.8s", bunny: "2s",
    cat: "3s", frog: "2.5s", fox: "2.5s",
    turtle: "4s", penguin: "2s",
  }[pet.species] || "2.5s";

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      {isLegendary && (
        <div style={{
          position: "absolute", inset: -4,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,215,0,0.35) 0%, rgba(255,215,0,0.15) 40%, transparent 70%)",
          animation: "legendaryPulse 2s ease-in-out infinite",
        }} />
      )}
      <svg width={size} height={size} viewBox={`0 0 ${viewSize} ${viewSize}`}
        style={{ imageRendering: "pixelated", position: "relative", zIndex: 1 }}>
        {isLegendary && (
          <g>
            <rect x={3} y={2} width={1} height={1} fill="#FFD700" opacity="0.8">
              <animate attributeName="opacity" values="0.8;0.2;0.8" dur="1.5s" repeatCount="indefinite" />
            </rect>
            <rect x={14} y={3} width={1} height={1} fill="#FFD700" opacity="0.6">
              <animate attributeName="opacity" values="0.3;0.9;0.3" dur="2s" repeatCount="indefinite" />
            </rect>
            <rect x={2} y={10} width={1} height={1} fill="#FFF0A0" opacity="0.7">
              <animate attributeName="opacity" values="0.7;0.1;0.7" dur="1.8s" repeatCount="indefinite" />
            </rect>
            <rect x={15} y={12} width={1} height={1} fill="#FFD700" opacity="0.5">
              <animate attributeName="opacity" values="0.5;1;0.5" dur="1.3s" repeatCount="indefinite" />
            </rect>
          </g>
        )}
        <g transform={`translate(${offset}, ${offset}) scale(${stageScale})`}
          style={{ animation: `${idleAnim} ${idleDur} ease-in-out infinite`, transformOrigin: "center bottom" }}>
          {renderPetPixels(pet.species, pet.stage, mood)}
        </g>
        {/* Blink — a skin-colored bar covers the eyes briefly */}
        <rect x={offset + 5 * stageScale} y={offset + 5 * stageScale}
          width={8 * stageScale} height={2 * stageScale} fill="transparent">
          <animate attributeName="fill" values="transparent;transparent;transparent;transparent;transparent;transparent;transparent;transparent;transparent;currentColor;transparent"
            dur="4s" repeatCount="indefinite" />
        </rect>
      </svg>
    </div>
  );
}

// ── Egg Picker Component ─────────────────────────────────────
export function EggPicker({ onPick }) {
  const [selected, setSelected] = useState(null);

  return (
    <div style={{ padding: "0 4px" }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🥚</div>
        <h2 style={{ fontSize: 20, fontFamily: "'Fredoka'", marginBottom: 4 }}>Choose Your Egg</h2>
        <p style={{ fontSize: 13, color: "var(--text-3)" }}>
          Each egg holds a mystery pet. The pattern is your only clue...</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
        {EGG_PRESETS.map((egg, i) => (
          <button key={i} onClick={() => setSelected(i)} style={{
            padding: 10, borderRadius: 14, border: "none", cursor: "pointer",
            background: selected === i ? "var(--gold-bg)" : "var(--bg-card)",
            outline: selected === i ? "3px solid var(--gold)" : "2px solid var(--bg-muted)",
            outlineOffset: 2, transition: "all .15s",
            display: "flex", justifyContent: "center", alignItems: "center",
          }}>
            {renderEgg(egg.color, egg.accent, egg.pattern, 48)}
          </button>
        ))}
      </div>

      <button
        disabled={selected === null}
        onClick={() => {
          if (selected === null) return;
          const egg = EGG_PRESETS[selected];
          const hinted = COLOR_SPECIES_MAP[egg.color] || "cat";
          const species = pickRandomSpecies(hinted);
          onPick({
            species,
            egg_pattern: egg.pattern,
            egg_color: egg.color,
            egg_accent: egg.accent,
            stage: 0,
            tasks_completed: 0,
            mood: "neutral",
            name: null,
          });
        }}
        style={{
          width: "100%", padding: 16, borderRadius: 14, border: "none",
          background: selected !== null ? "var(--gold)" : "var(--bg-muted)",
          cursor: selected !== null ? "pointer" : "default",
          fontSize: 15, fontWeight: 700, fontFamily: "'Fredoka'",
          color: selected !== null ? "var(--text)" : "var(--text-3)",
          boxShadow: selected !== null ? "var(--shadow-sm)" : "none",
          transition: "all .2s",
        }}>
        {selected !== null ? "Pick This Egg" : "Tap an egg to select it"}
      </button>
    </div>
  );
}

// ── Name Your Pet Modal ──────────────────────────────────────
export function NamePetModal({ species, onName }) {
  const [name, setName] = useState("");
  const sp = SPECIES[species] || SPECIES.cat;

  return (
    <div style={{ textAlign: "center", padding: "20px 4px" }}>
      <div style={{ fontSize: 42, marginBottom: 8 }}>{sp.emoji}</div>
      <h2 style={{ fontSize: 20, fontFamily: "'Fredoka'", marginBottom: 4 }}>Your egg hatched!</h2>
      <p style={{ fontSize: 14, color: "var(--text-3)", marginBottom: 20 }}>
        It's a <strong>{sp.name}</strong>! What would you like to name them?</p>
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder={`Name your ${sp.name}...`}
        maxLength={20}
        style={{
          width: "100%", padding: "14px 18px", borderRadius: 14,
          border: "2px solid var(--bg-muted)", fontSize: 16,
          background: "var(--bg-card)", outline: "none", fontFamily: "inherit",
          textAlign: "center", marginBottom: 16,
        }}
      />
      <button
        disabled={!name.trim()}
        onClick={() => name.trim() && onName(name.trim())}
        style={{
          width: "100%", padding: 16, borderRadius: 14, border: "none",
          background: name.trim() ? "var(--green)" : "var(--bg-muted)",
          cursor: name.trim() ? "pointer" : "default",
          fontSize: 15, fontWeight: 700, fontFamily: "'Fredoka'",
          color: name.trim() ? "white" : "var(--text-3)",
          transition: "all .2s",
        }}>
        {name.trim() ? `Welcome, ${name.trim()}!` : "Enter a name..."}
      </button>
    </div>
  );
}

// ── Supabase Hook ────────────────────────────────────────────
export function usePets(supabase) {
  const [pets, setPets] = useState([]);

  const fetchPets = useCallback(async () => {
    const { data } = await supabase.from("pets").select("*");
    if (data) setPets(data);
  }, [supabase]);

  useEffect(() => {
    fetchPets();
    const sub = supabase.channel("pets-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "pets" }, () => fetchPets()).subscribe();
    return () => supabase.removeChannel(sub);
  }, [fetchPets, supabase]);

  const createPet = async (personId, petData) => {
    await supabase.from("pets").insert({ person_id: personId, ...petData });
    await fetchPets();
  };

  const updatePet = async (petId, updates) => {
    await supabase.from("pets").update(updates).eq("id", petId);
    await fetchPets();
  };

  const incrementTasks = async (petId, currentCount) => {
    const newCount = currentCount + 1;
    const updates = { tasks_completed: newCount };

    // Check for evolution
    for (let i = STAGE_THRESHOLDS.length - 1; i >= 0; i--) {
      if (newCount >= STAGE_THRESHOLDS[i]) {
        updates.stage = i;
        break;
      }
    }

    await supabase.from("pets").update(updates).eq("id", petId);
    await fetchPets();
    return updates;
  };

  const releasePet = async (pet) => {
    await supabase.from("pet_collection").insert({
      person_id: pet.person_id,
      species: pet.species,
      name: pet.name,
    });
    await supabase.from("pets").delete().eq("id", pet.id);
    await fetchPets();
  };

  const getPersonPet = (personId) => pets.find(p => p.person_id === personId);

  return { pets, createPet, updatePet, incrementTasks, releasePet, getPersonPet, fetchPets };
}